import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { MEMBERS } from "../mock";
import Reveal from "./Reveal";

const ChatPhone = ({ card }) => (
  <div className="flex flex-col h-full bg-gradient-to-b from-[#17150f] to-[#0e0d0a]">
    {/* top bar */}
    <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06] bg-white/[0.02]">
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#f6cd8b] to-[#c6893f] flex items-center justify-center font-display font-700 text-[#3a2a10] text-sm">
        {card.name.charAt(0)}
      </div>
      <div>
        <div className="text-white text-[0.85rem] font-600">{card.name}</div>
        <div className="text-[#5eae7a] text-[0.7rem] flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-[#5eae7a]" /> online
        </div>
      </div>
    </div>
    {/* messages */}
    <div className="flex-1 p-4 space-y-3 overflow-hidden">
      {card.messages.map((m, i) => (
        <div key={i} className={`flex ${m.from === "me" ? "justify-end" : "justify-start"}`}>
          <div
            className={`max-w-[78%] px-3.5 py-2.5 text-[0.82rem] leading-snug ${
              m.from === "me"
                ? "bg-[#2f6a4a] text-white rounded-2xl rounded-br-md"
                : "bg-white/[0.06] text-[#e6e2d8] rounded-2xl rounded-bl-md"
            }`}
          >
            {m.text}
            <div className={`text-[0.62rem] mt-1 flex items-center gap-0.5 justify-end ${m.from === "me" ? "text-white/70" : "text-white/40"}`}>
              09:{35 + i * 6}
              {m.from === "me" && <Check className="w-3 h-3" strokeWidth={3} />}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const TradesPhone = ({ card }) => (
  <div className="flex flex-col h-full bg-gradient-to-b from-[#141712] to-[#0c0d0a]">
    <div className="px-4 py-3 border-b border-white/[0.06] bg-white/[0.02]">
      <div className="text-white text-[0.85rem] font-600">{card.name}</div>
      <div className="text-[#8a8478] text-[0.68rem]">Live copied trades</div>
    </div>
    <div className="flex-1 px-3 py-2 overflow-hidden">
      <div className="space-y-1.5">
        {card.trades.map((t, i) => (
          <div key={i} className="flex items-center justify-between text-[0.72rem] font-mono py-1 border-b border-white/[0.04]">
            <div className="flex items-center gap-2">
              <span className={`uppercase font-semibold ${t.side === "buy" ? "text-[#4fa3ff]" : "text-[#ff6b6b]"}`}>{t.side}</span>
              <span className="text-[#c7c1b5]">{t.pair}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[#7c7669]">{t.price}</span>
              <span className="text-[#5eae7a] font-semibold w-16 text-right">+{t.pl}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="px-4 py-3 border-t border-white/[0.06] bg-white/[0.02] grid grid-cols-3 gap-2 text-center">
      <div>
        <div className="text-[0.6rem] text-[#8a8478] uppercase tracking-wide">Start</div>
        <div className="text-[0.78rem] text-[#c7c1b5] font-mono">{card.account.starting}</div>
      </div>
      <div>
        <div className="text-[0.6rem] text-[#8a8478] uppercase tracking-wide">Drawdown</div>
        <div className="text-[0.78rem] text-[#ff6b6b] font-mono">{card.account.drawdown}</div>
      </div>
      <div>
        <div className="text-[0.6rem] text-[#8a8478] uppercase tracking-wide">Balance</div>
        <div className="text-[0.78rem] text-[#5eae7a] font-mono">{card.account.current}</div>
      </div>
    </div>
  </div>
);

const Phone = ({ card, className = "" }) => (
  <div className={`relative rounded-[32px] border border-white/10 bg-[#050505] p-2 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] ${className}`}>
    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-20 h-1.5 rounded-full bg-white/10 z-10" />
    <div className="relative aspect-[9/17] rounded-[24px] overflow-hidden">
      {card.type === "chat" ? <ChatPhone card={card} /> : <TradesPhone card={card} />}
    </div>
  </div>
);

const MemberResults = () => {
  const [active, setActive] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const cards = MEMBERS.cards;
  const n = cards.length;

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const go = (dir) => setActive((a) => (a + dir + n) % n);
  const spread = isMobile ? 120 : 220;

  return (
    <section className="relative py-24 sm:py-28 border-t border-white/[0.05] overflow-hidden">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        <Reveal className="text-center max-w-2xl mx-auto">
          <span className="section-label">{MEMBERS.label}</span>
          <h2 className="font-display font-800 text-white text-[2rem] sm:text-[2.9rem] leading-[1.05] mt-4 tracking-tight">
            {MEMBERS.title}
          </h2>
          <p className="text-[#a49e92] text-[1rem] leading-relaxed mt-5">{MEMBERS.subtitle}</p>
        </Reveal>

        {/* Carousel */}
        <Reveal delay={120} className="mt-16">
          <div className="relative flex items-center justify-center min-h-[440px]">
            {/* arrows */}
            <button
              onClick={() => go(-1)}
              className="absolute left-0 sm:left-4 z-20 w-11 h-11 rounded-full border border-white/12 bg-[#0a0908]/80 backdrop-blur flex items-center justify-center text-white hover:bg-white/10 hover:border-white/25 transition"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => go(1)}
              className="absolute right-0 sm:right-4 z-20 w-11 h-11 rounded-full border border-white/12 bg-[#0a0908]/80 backdrop-blur flex items-center justify-center text-white hover:bg-white/10 hover:border-white/25 transition"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* stacked phones */}
            <div className="relative w-full flex items-center justify-center overflow-hidden" style={{ perspective: "1400px" }}>
              {cards.map((card, i) => {
                let offset = i - active;
                if (offset > n / 2) offset -= n;
                if (offset < -n / 2) offset += n;
                const abs = Math.abs(offset);
                const isCenter = offset === 0;
                return (
                  <div
                    key={i}
                    className="absolute transition-all duration-500 ease-out"
                    style={{
                      transform: `translateX(${offset * spread}px) scale(${isCenter ? 1 : 0.82}) rotateY(${offset * -6}deg)`,
                      opacity: abs > 1 ? 0 : isCenter ? 1 : 0.45,
                      zIndex: 10 - abs,
                      filter: isCenter ? "none" : "blur(1px)",
                      pointerEvents: isCenter ? "auto" : "none",
                    }}
                  >
                    <Phone card={card} className="w-[220px] sm:w-[248px]" />
                  </div>
                );
              })}
            </div>
          </div>

          {/* dots */}
          <div className="flex items-center justify-center gap-2 mt-10">
            {cards.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === active ? "w-7 bg-[#d69b4e]" : "w-1.5 bg-white/20 hover:bg-white/40"
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </Reveal>

        <Reveal delay={200} className="max-w-3xl mx-auto mt-14">
          <p className="text-[#6e685c] text-[0.78rem] leading-relaxed text-center">{MEMBERS.disclaimer}</p>
        </Reveal>
      </div>
    </section>
  );
};

export default MemberResults;
